<template>
  <div
    ref="$el"
    class="rich-text"
  >
    <UiComponentWrapper
      v-for="(item, index) in content"
      ref="$wrappers"
      :key="item.id"
      class="row"
      :class="{
        'row-customer-story':
          item.__typename === 'ContentCustomerStoryAndQuoteRecord',
        'row-image': item.__typename === 'ImageRecord',
        'row-tag': item.__typename === 'ContentTagRecord',
        'row-text':
          item.__typename === 'ContentTextHtmlRecord'
          || item.__typename === 'TextRecord',
        'row-round-up': item.__typename === 'ContentRoundUpRecord',
        'row-video': item.__typename === 'ContentVideoOrYoutubeEmbedRecord',
        'inline-content-card-record':
          item.__typename === 'inlineContentCardRecord',
        'inline-text-line-record': item.__typename === 'inlineTextCtaRecord',
      }"
    >
      <UiComponentTag
        v-if="item.__typename === 'ContentTagRecord'"
        type="secondary"
      >
        {{
          item.name
        }}
      </UiComponentTag>
      <UiComponentWrapper
        v-if="item.__typename === 'CalltoactionRecord'"
        class="call-to-action-record"
        :class="{ full: !!item.full }"
      >
        <MoleculeCallToAction
          :call-to-action="{
            action:
              item.action || isLogged ? 'Upgrade clicked' : 'Signup clicked',
            label: item.label || 'Small section CTA',
            slot: item.cta,
            to: item.link,
          }"
          :pattern-desktop="item.patternDesktop"
          :pattern-mobile="item.patternMobile"
        >
          <template #heading>
            {{ item.title }}
          </template>
          <template #paragraph>
            {{ item.text }}
          </template>
        </MoleculeCallToAction>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="
          item.__typename === 'ContentCustomerStoryAndQuoteRecord'
            && item.customer.quote
        "
        class="inner"
      >
        <UiComponentImage
          alt="quote"
          class="illustration"
          :expected-size="104"
          :height="2400"
          source="https://livestorm.imgix.net/1127/1676296023-quote-roundedcorner-lsblue300-2.png"
          :width="2400"
        />
        <UiComponentWrapper class="customer">
          <UiComponentImage
            v-if="item.customer.logo"
            :alt="item.customer.logo.alt"
            class="logo"
            :expected-size="20"
            :extra-params="{
              trim: 'color',
            }"
            :height="item.customer.logo.height"
            :orientation="
              item.customer.logo.width >= item.customer.logo.height
                ? 'portrait'
                : 'landscape'
            "
            :source="item.customer.logo.url"
            use-natural-height
            use-natural-width
            :width="item.customer.logo.width"
          />
          <UiComponentParagraph
            v-if="item.customer.keyNumbers"
            class="highlight"
          >
            <UiComponentIcon type="trending-up" />{{
              item.customer.keyNumbers
            }}
          </UiComponentParagraph>
        </UiComponentWrapper>
        <UiComponentWrapper class="quote">
          "{{ item.customer.quote }}"
        </UiComponentWrapper>
        <UiComponentWrapper class="author">
          <UiComponentImage
            v-if="item.customer.authorAvatar"
            :alt="item.customer.authorAvatar.alt"
            :expected-size="48"
            :height="item.customer.authorAvatar.height"
            :source="item.customer.authorAvatar.url"
            :width="item.customer.authorAvatar.width"
          />
          <UiComponentWrapper class="information">
            <UiComponentParagraph>{{ item.customer.author }}</UiComponentParagraph>
            <UiComponentParagraph>{{ item.customer.authorPosition }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentWrapper class="action">
          <AtomButton
            :to="item.cta.to"
            type="text"
          >
            {{ item.cta.slot }}<UiComponentIcon type="arrow-right" />
          </AtomButton>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper v-if="item.__typename === 'ContentYoutubeVideoRecord'">
        <div class="youtube-iframe">
          <iframe
            v-if="isVisible"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            frameborder="0"
            height="100%"
            loading="lazy"
            :src="`${item.youtubeLink}`"
            title="YouTube video player"
            width="100%"
          />
        </div>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="item.__typename === 'MoleculeDefinitionRecord'"
        class="definition-record"
      >
        <MoleculeDefinition :title="item.title">
          <div v-html="item.content" />
        </MoleculeDefinition>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="item.__typename === 'MoleculeSmallCallToActionRecord'"
        class="call-to-action-record"
      >
        <MoleculeCallToAction
          :call-to-action="{
            action: setActionBasedOnSlug(item.callToAction.slug),
            slot: item.callToAction.slot,
            to: item.callToAction.slug,
          }"
          :pattern-desktop="item.patternDesktop"
          :pattern-mobile="item.patternMobile"
          @button:click="emit('cta:click', $event)"
        >
          <template #heading>
            {{ item.heading }}
          </template>
          <template #paragraph>
            {{ item.paragraph }}
          </template>
        </MoleculeCallToAction>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="item.__typename === 'ContentRoundUpRecord'"
        class="round-up-record"
      >
        <MoleculeExpertRoundUp
          :company="item.expert.company"
          :name="item.expert.name"
          :picture="item.expert.picture"
          :quote="item.quote"
          :social-medias="[
            {
              app: 'facebook',
              icon: 'https://livestorm.imgix.net/1127/1616598215-facebook.svg?h=20&w=auto',
              url: item.expert.facebook,
            },
            {
              app: 'twitter',
              icon: 'https://livestorm.imgix.net/1127/1616598155-twitter.svg?h=20&w=auto',
              url: item.expert.twitter,
            },
            {
              app: 'linkedin',
              icon: 'https://livestorm.imgix.net/1127/1616598194-linkedin.svg?h=20&w=auto',
              url: item.expert.linkedin,
            },
            {
              app: 'instagram',
              icon: 'https://livestorm.imgix.net/1127/1616598242-instagram.svg?h=20&w=auto',
              url: item.expert.instagram,
            },
          ]"
          :url="item.expert.url"
        />
      </UiComponentWrapper>
      <UiComponentWrapper
        v-else-if="item.__typename === 'ContentTextHtmlRecord'"
        class="text-record"
      >
        <div v-html="$md.render(item.text)" />
      </UiComponentWrapper>
      <UiComponentWrapper
        v-else-if="item.__typename === 'ContentQuoteRecord'"
        class="blog-quote"
      >
        <MoleculeQuote :text="item.text" />
      </UiComponentWrapper>
      <UiComponentWrapper
        v-else-if="item.__typename === 'ContentImageTitleTextRecord'"
        class="image-with-title-text"
        :class="{ reverse: item.imagePosition === 'right' }"
      >
        <UiComponentImage
          :alt="item.image.alt"
          :expected-size="imageWithTextSize"
          :height="item.image.height"
          :source="item.image.url"
          :width="item.image.width"
        />
        <UiComponentWrapper class="body">
          <UiComponentHeading :level="4">
            {{ item.title }}
          </UiComponentHeading>
          <UiComponentParagraph>{{ item.text }}</UiComponentParagraph>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-else-if="item.__typename === 'ContentProductFeatureRecord'"
        class="product-feature"
      >
        <MoleculeBlogFeature
          :content="{
            button: {
              action: setActionBasedOnSlug(primaryButton.to),
              slot: primaryButton.slot,
              to: primaryButton.to,
            },
            image: item.feature.imageOnlyUsedForWowFeatures,
            text: item.feature.productAnnouncementBlockText,
            title: item.feature.productAnnouncementBlockTitle,
          }"
        />
      </UiComponentWrapper>
      <UiComponentWrapper v-else-if="item.__typename === 'inlineTextCtaRecord'">
        <MoleculeInlineTextCta :button="{ event: item.event, to: item.to }">
          {{
            item.text
          }}
        </MoleculeInlineTextCta>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-else-if="item.__typename === 'inlineContentCardRecord'"
      >
        <OrganismContentCard
          as="paragraph"
          :content="item"
        />
      </UiComponentWrapper>
      <UiComponentImage
        v-else-if="item.__typename === 'ImageRecord' && item.image"
        :alt="item.image.alt || 'image'"
        class="image-record image"
        :expected-size="widths[index]"
        :height="item.image.height"
        :source="item.image.url"
        :width="item.image.width"
      />
      <UiComponentWrapper
        v-else-if="item.__typename === 'ImageWithTextRecord'"
        class="image-with-text-record"
        :class="item.imagePosition"
      >
        <UiComponentImage
          :alt="item.image.alt || 'image'"
          class="image"
          :expected-size="widths[index] / divider"
          :height="item.image.height"
          :source="item.image.url"
          :width="item.image.width"
        />
        <UiComponentWrapper class="content">
          <span v-html="$md.render(item.content)" />
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-else-if="item.__typename === 'TextRecord'"
        class="text-record"
      >
        <span v-html="$md.render(item.text)" />
      </UiComponentWrapper>

      <UiComponentWrapper
        v-else-if="
          item.__typename === 'ContentVideoOrYoutubeEmbedRecord'
            && !item.title
            && (item.youtubeEmbed || item.video)
        "
        class="video-or-youtube-embed-record"
      >
        <UiComponentWrapper class="asset">
          <UiComponentWrapper
            v-if="item.youtubeEmbed"
            class="iframe"
          >
            <iframe
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              frameborder="0"
              height="100%"
              loading="lazy"
              :src="`https://www.youtube.com/embed/${item.youtubeEmbed.providerUid}`"
              title="YouTube video player"
              width="100%"
            />
          </UiComponentWrapper>
          <OrganismPlayerVideo
            v-if="item.video"
            :poster-src="item.video.video.thumbnailUrl"
            :video-src="item.video.video.streamingUrl"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-else-if="
          item.__typename === 'ContentVideoOrYoutubeEmbedRecord'
            && item.title
            && (item.youtubeEmbed || item.video)
        "
        class="video-or-youtube-embed-record-with-title"
      >
        <UiComponentWrapper class="asset">
          <UiComponentWrapper
            v-if="item.youtubeEmbed"
            class="iframe"
          >
            <iframe
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              frameborder="0"
              height="100%"
              loading="lazy"
              :src="`https://www.youtube.com/embed/${item.youtubeEmbed.providerUid}`"
              title="YouTube video player"
              width="100%"
            />
          </UiComponentWrapper>
          <OrganismPlayerVideo
            v-if="item.video"
            :poster-src="item.video.video.thumbnailUrl"
            :video-src="item.video.video.streamingUrl"
          />
        </UiComponentWrapper>
        <UiComponentWrapper class="content">
          <UiComponentHeading
            as="paragraph"
            :level="2"
          >
            {{ item.title }}
          </UiComponentHeading>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentWrapper>
  </div>
</template>

<script setup>
// Emitter
const emit = defineEmits([
  'cta:click',
])

// Props
const props = defineProps({
  content: {
    required: true,
    type: Array,
  },
  primaryButton: {
    default() {
      return {}
    },
    required: false,
    type: Object,
  },
})

// Composables
const { isLogged } = useIsLoggedIn()
const { $md } = useMarkdown()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Refs
const $el = ref(null)
const $wrappers = ref(null)
const divider = ref(2)
const imageWithTextSize = ref(290)
const isVisible = ref(false)
const observer = ref(null)
const widths = ref((props.content || []).map(() => 1328))

// Mounted
function onObserve() {
  observer.value = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      isVisible.value = true
      unobserve()
    }
  })
  observer.value.observe($el.value)
}

function onResize() {
  const { clientWidth } = document.documentElement
  if (clientWidth < 768) imageWithTextSize.value = clientWidth - 16
  else if (clientWidth < 1328) imageWithTextSize.value = clientWidth - 32
  else imageWithTextSize.value = 290
  divider.value = clientWidth < 1328 ? 1 : 2
  widths.value = ($wrappers.value || []).map(({ $el }) =>
    $el.offsetWidth > 1328 ? 1328 : $el.offsetWidth,
  )
}

function setActionBasedOnSlug(slug) {
  switch (slug) {
    case `https://${publicRuntimeConfig.appBaseUrl}/#/signup`:
      return 'Signup clicked'
    case `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`:
      return 'Upgrade clicked'
    case '#form-demo':
      return 'Demo clicked'
    default:
      return undefined
  }
}

function unobserve() {
  observer.value.disconnect()
  observer.value = null
}

// Lifecycle hooks
onBeforeUnmount(() => {
  if (observer.value) {
    unobserve()
  }

  window.removeEventListener('resize', onResize)
})

onMounted(() => {
  window.addEventListener('resize', onResize)

  nextTick(() => {
    onObserve()
    onResize()
  })
})
</script>

<style lang="postcss" scoped>
.rich-text {
  @apply flex-col px-0 !important;

  :deep(.row) {
    @apply flex-col mt-16;

    &:first-child {
      @apply mt-0;
    }

    &.row-customer-story {
      @apply mt-10;

      .inner {
        @apply relative flex-col p-8 pb-4 border-2 border-grey-blue-200 rounded-4xl;

        @screen md {
          @apply p-10 pb-6;
        }

        .illustration {
          @apply absolute right-[26px] -top-8 rounded-none;

          @screen md {
            @apply -top-12;
          }
        }

        .customer {
          @apply flex-col self-start w-auto;

          @screen md {
            @apply absolute bottom-[82px] right-10;
          }

          .logo {
            @apply rounded-none;
          }

          .highlight {
            @apply font-medium text-spring-green-600 text-sm leading-7;

            .icon {
              @apply text-spring-green-600 text-xs leading-none mr-2;
            }
          }
        }

        .quote {
          @apply mt-2 text-grey-blue-600 text-origin leading-7;

          @screen md {
            @apply mt-0;
          }
        }

        .author {
          @apply mt-4;

          @screen md {
            @apply mt-6;
          }

          .avatar {
            @apply flex-shrink-0 rounded-full;
          }

          .information {
            @apply flex-col ml-3;

            p {
              @apply font-medium text-winter-green-900 text-sm leading-6;

              & + p {
                @apply font-normal text-grey-blue-600;
              }
            }
          }
        }

        .action {
          @apply mt-2;

          .button {
            @apply h-[50px] px-0 font-bold;
          }
        }
      }
    }

    &.row-round-up {
      @apply mt-14;
    }

    &.row-text + .row-round-up {
      @apply mt-6;
    }

    &.row-round-up + .row-text {
      @apply mt-14 !important;
    }

    .image {
      @apply bg-grey-blue-200;
    }

    .image-record,
    .image-with-text-record,
    .text-record {
      @apply flex-col;
    }

    .image-with-title-text {
      @apply flex-col;

      @screen xl {
        @apply flex-row;
      }

      &.reverse {
        @screen xl {
          @apply flex-row-reverse;
        }

        .body {
          @screen xl {
            @apply ml-0 mr-10;
          }
        }
      }

      picture {
        @apply w-full h-full flex-shrink-0;

        @screen xl {
          @apply h-auto;
          width: 290px;
        }
      }

      .body {
        @apply flex-col;

        @screen xl {
          @apply ml-10;
        }

        h4 {
          @apply mt-6;

          @screen xl {
            @apply mt-0;
          }
        }

        p {
          @apply mt-4;

          @screen xl {
            @apply mt-0;
          }
        }
      }
    }

    .image-with-text-record,
    .text-record {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        @apply text-winter-green-900 whitespace-pre-line;
      }

      h1,
      h2 {
        @apply font-normal;

        b,
        strong {
          @apply font-normal text-livestorm-blue-700;
        }
      }

      h3,
      h4,
      h5,
      h6 {
        @apply font-medium;

        b,
        strong {
          @apply font-medium;
        }
      }

      h1 {
        @apply text-4xl leading-14;

        @screen mdminus {
          @apply text-5xl leading-16;
        }

        @screen mdplus {
          @apply text-6xl leading-20;
        }
      }

      h2 {
        @apply text-3xl leading-10;

        @screen mdminus {
          @apply font-medium text-3xl leading-10;
        }

        @screen mdplus {
          @apply font-normal text-5xl leading-16;
        }

        b,
        strong {
          @screen mdminus {
            @apply font-medium;
          }

          @screen mdplus {
            @apply font-normal;
          }
        }
      }

      h3 {
        @apply text-2xl leading-8;

        @screen mdminus {
          font-size: 28px;
          line-height: 34px;
        }

        @screen mdplus {
          @apply text-3xl leading-10;
        }
      }

      h4 {
        @apply text-xl leading-7;

        @screen mdminus {
          @apply text-2xl leading-8;
        }

        @screen mdplus {
          @apply text-2xl leading-8;
        }
      }

      h5 {
        @apply text-origin leading-7;

        @screen mdminus {
          @apply text-xl;
        }

        @screen mdplus {
          @apply text-xl;
        }
      }

      h6 {
        @apply text-origin leading-7;
      }

      p {
        @apply font-normal text-winter-green-900 text-origin leading-7;
      }

      blockquote {
        @apply p-8 bg-livestorm-blue-100 text-livestorm-blue-700 rounded-4xl;

        @screen md {
          @apply p-10;
        }

        p {
          @apply inline text-center text-livestorm-blue-700 text-xl leading-[160%] !important;

          &::before,
          &::after {
            @apply inline;
          }

          &::before {
            @apply content-['“'];
          }

          &::after {
            @apply content-['”'];
          }
        }
      }

      ol {
        li {
          @apply font-normal text-winter-green-900 text-origin leading-7 list-decimal list-inside;
        }
      }

      ul {
        li {
          @apply block font-normal text-winter-green-900 text-origin leading-7;

          &:before {
            @apply inline-block w-3 h-3 bg-livestorm-blue-700 mr-3;
            border-radius: 3px;
            content: "";
          }
        }
      }

      strong {
        @apply font-medium;
      }

      a {
        @apply text-livestorm-blue-700;

        &:hover {
          @apply text-livestorm-blue-800;
        }
      }

      h1 + h1,
      h1 + h2,
      h1 + h3,
      h2 + h1,
      h2 + h2,
      h2 + h3,
      h3 + h1,
      h3 + h2,
      h3 + h3,
      h4 + h1,
      h4 + h2,
      h4 + h3,
      h5 + h1,
      h5 + h2,
      h5 + h3,
      h6 + h1,
      h6 + h2,
      h6 + h3,
      h1 + blockquote,
      h2 + blockquote,
      h3 + blockquote,
      h1 + p,
      h2 + p,
      h3 + p,
      h1 + ul,
      h2 + ul,
      h3 + ul,
      h1 + ol,
      h2 + ol,
      h3 + ol {
        @apply mt-4;
      }

      h1 + h4,
      h1 + h5,
      h1 + h6,
      h2 + h4,
      h2 + h5,
      h2 + h6,
      h3 + h4,
      h3 + h5,
      h3 + h6,
      h4 + h4,
      h4 + h5,
      h5 + h6,
      h5 + h4,
      h5 + h5,
      h5 + h6,
      h5 + h6,
      h6 + h4,
      h6 + h5,
      h6 + h6,
      h4 + blockquote,
      h5 + blockquote,
      h6 + blockquote,
      h4 + p,
      h5 + p,
      h6 + p,
      h4 + ul,
      h5 + ul,
      h6 + ul,
      h4 + ol,
      h5 + ol,
      h6 + ol,
      blockquote + ul,
      blockquote + ol,
      p + ul,
      p + ol {
        @apply mt-4;
      }

      blockquote + h1,
      blockquote + h2,
      p + h1,
      p + h2,
      ul + h1,
      ul + h2,
      ol + h1,
      ol + h2 {
        @apply mt-16;
      }

      blockquote + blockquote,
      blockquote + h3,
      blockquote + h4,
      blockquote + h5,
      blockquote + h6,
      p + blockquote,
      p + h3,
      p + h4,
      p + h5,
      p + h6,
      ul + h3,
      ul + h4,
      ul + h5,
      ul + h6,
      ol + h3,
      ol + h4,
      ol + h5,
      ol + h6 {
        @apply mt-8;
      }

      blockquote + p,
      p + p,
      ul + p,
      ol + p,
      li + li {
        @apply mt-4;
      }
    }

    .image-with-text-record {
      @apply flex-col-reverse;

      @screen xl {
        @apply flex-row;
      }

      .image {
        @apply w-full;

        @screen xl {
          @apply w-1/2;
        }
      }

      .content {
        @apply w-full mb-10;

        @screen xl {
          @apply items-center mb-0 w-1/2 pl-10;
        }
      }

      &.right {
        @screen xl {
          @apply flex-row-reverse;
        }

        .content {
          @screen xl {
            @apply pl-0 pr-10;
          }
        }
      }
    }

    .call-to-action-record {
      .button {
        @apply text-center !important;
      }
    }

    .call-to-action-record,
    .definition-record {
      @screen xl {
        padding: 0 110px;
      }

      &.full {
        @screen xl {
          padding: 0 40px !important;
        }
      }
    }

    .tag {
      @apply self-start;
    }
  }

  :deep(.youtube-iframe) {
    padding-bottom: 56.25%;
    @apply relative w-full h-full overflow-hidden rounded-4xl;

    iframe {
      @apply absolute inset-0 w-full h-full;
    }
  }

  :deep(.row-tag + .row-text) {
    @apply mt-6 !important;
  }

  :deep(.video-or-youtube-embed-record) {
    @apply flex-row overflow-hidden;
    border-radius: 16px;

    @screen md {
      border-radius: 24px;
    }

    @screen xl {
      border-radius: 32px;
    }

    .asset {
      .video {
        @apply h-auto;

        video {
          @apply self-start h-auto rounded-none !important;
        }
      }

      .iframe {
        @apply relative;
        padding-bottom: 56.25%;

        iframe {
          @apply absolute top-0 left-0 w-full h-full;
        }
      }
    }
  }

  :deep(.video-or-youtube-embed-record-with-title) {
    @apply flex-col p-4 pb-6 border-2 border-grey-blue-200 rounded-4xl;

    @screen md {
      @apply p-6;
    }

    @screen xl {
      @apply flex-row items-center pr-10;
    }

    .asset {
      @apply flex-shrink-0 overflow-hidden;
      border-radius: 18px;

      @screen xl {
        max-width: 486px;
      }

      .video {
        @apply h-auto;

        video {
          @apply self-start h-auto rounded-none !important;
        }
      }

      .iframe {
        @apply relative;
        padding-bottom: 56.25%;

        iframe {
          @apply absolute top-0 left-0 w-full h-full;
        }
      }
    }

    .content {
      @apply flex-col mt-4;

      @screen md {
        @apply mt-6;
      }

      @screen xl {
        @apply mt-0 ml-10;
      }

      .h2 {
        @apply text-winter-green-900 !important;

        strong {
          color: #be3a37 !important;
        }
      }
    }
  }
}
</style>
